import type {
  ConfirmPaymentData,
  ConfirmSetupData,
  PaymentIntentResult,
  Stripe,
} from "@stripe/stripe-js";

import { paywallCheckoutApi } from "@/react/api/paywallCheckoutApi";
import { CheckoutError } from "@/react/components/Paywalls/Checkout/CheckoutError";
import type { StripeElements } from "@/react/components/Paywalls/StripeElements/types";

import type { MemberAddressState } from "../types";

interface CreateSetupIntentResult {
  setupIntentClientSecret: string;
  setupIntentId: string;
}

export const createSetupIntent = async (
  communityId: number,
  paywallPriceId: number,
  checkoutIntentToken?: string,
): Promise<CreateSetupIntentResult> => {
  const response = await paywallCheckoutApi.prepare(
    communityId,
    paywallPriceId,
    checkoutIntentToken,
  );
  if (!response || !response.ok) {
    const error = await response?.json?.();
    throw CheckoutError.fromPaywallError("setupPaymentMethod", error);
  }

  const responseBody = (await response.json()) as {
    setup_intent: { id: string; client_secret: string };
  };

  const setupIntentClientSecret = responseBody.setup_intent.client_secret;
  if (!setupIntentClientSecret) {
    throw CheckoutError.generic("setupPaymentMethod.missingClientSecret");
  }

  return {
    setupIntentId: responseBody.setup_intent.id,
    setupIntentClientSecret,
  };
};

/**
 * Build billing details from member address.
 *
 * When we opt out of collecting fields in the PaymentElement (via fields.billing_details: "never"),
 * we must pass those values in confirmParams.payment_method_data.billing_details.
 *
 * This is used by both payment intent and setup intent confirmations.
 *
 * @see https://docs.stripe.com/js/payment_intents/confirm_payment#confirm_payment_intent-options-confirmParams-payment_method_data
 */
const buildBillingDetails = (memberAddress: MemberAddressState) => ({
  email: memberAddress.email,
  name: memberAddress.name ?? "",
  address: {
    city: memberAddress.city,
    country: memberAddress.country,
    line1: memberAddress.line1,
    line2: memberAddress.line2,
    postal_code: memberAddress.postalCode,
    state: memberAddress.state,
  },
});

/**
 * Build confirmParams for Stripe's confirmPayment and confirmSetup.
 * Both APIs accept the same structure for billing details.
 */
export const buildConfirmParams = (
  memberAddress: MemberAddressState,
  returnUrl: string,
): ConfirmPaymentData & ConfirmSetupData => ({
  return_url: returnUrl,
  payment_method_data: {
    billing_details: buildBillingDetails(memberAddress),
  },
});

export interface ConfirmPaymentIntentProps {
  caller: string;
  confirmParams: ConfirmPaymentData;
  paymentIntentClientSecret: string;
  stripe: Stripe;
  stripeElements?: StripeElements;
}

export const confirmPaymentIntent = async ({
  caller,
  confirmParams,
  paymentIntentClientSecret,
  stripe,
  stripeElements,
}: ConfirmPaymentIntentProps): Promise<void> => {
  const paymentIntentResult = await stripe.retrievePaymentIntent(
    paymentIntentClientSecret,
  );
  if (paymentIntentResult.error) {
    throw CheckoutError.fromStripeError(caller, paymentIntentResult.error);
  }

  const paymentIntent = paymentIntentResult.paymentIntent;

  if (paymentIntent?.status === "succeeded") {
    return;
  }

  let confirmPaymentResult: PaymentIntentResult;
  if (stripeElements) {
    confirmPaymentResult = await stripe.confirmPayment({
      clientSecret: paymentIntentClientSecret,
      confirmParams,
      elements: stripeElements,
      redirect: "if_required",
    });
  } else {
    confirmPaymentResult = await stripe.confirmPayment({
      clientSecret: paymentIntentClientSecret,
      confirmParams,
      redirect: "if_required",
    });
  }

  if (confirmPaymentResult.error) {
    throw CheckoutError.fromStripeError(caller, confirmPaymentResult.error);
  }
};
